// Reader-facing text for the blog widgets. The moderation page and the API are
// English.
//
// Deliberately free of DOM references, so the Worker-side test suite can import
// it and check every error code the API can emit against a translation.

export const LANGS = ["en", "ko"] as const;
export type Lang = (typeof LANGS)[number];

const en = {
  lang: "en" as Lang,
  ui: {
    loading: "Loading comments…",
    empty: "No comments yet.",
    loadFailed: "Comments couldn't be loaded.",
    nameLabel: "Name",
    emailLabel: "Email (only for Gravatar; never shown)",
    bodyLabel: "Comment",
    submit: "Post comment",
    posting: "Posting…",
    posted: "Thanks — your comment is posted.",
    pending: "Thanks — your comment is awaiting moderation.",
    postFailed: "Couldn't post your comment.",
    spamTimeout: "Spam check didn't finish. Please reload and try again.",
    missingSlug: "Comments: missing data-slug.",
    /** Stands in for data-owner-name, so the mark never reads as unattributed. */
    owner: "owner",
    hearted: (owner: string) => `Liked by ${owner}`,
    reply: "Reply",
    replyTo: (name: string) => `Replying to ${name}`,
    cancelReply: "Cancel",
  },
  subscribe: {
    checking: "Checking…",
    saving: "Saving…",
    subscribedState: "You're subscribed.",
    unsubscribedState: "You're not subscribed.",
    subscribe: "Subscribe",
    unsubscribe: "Unsubscribe",
    failed: "That didn't work. Please try again.",
    unconfigured: "Signing in isn't set up on this site yet.",
  },
  // Keyed by the `code` field of an API error response; see errorText.
  errors: {
    generic: "Something went wrong. Please try again.",
    bad_json: "That request wasn't understood.",
    rate_limited: "You're posting a little quickly — try again in a minute.",
    slug_missing: "Missing page identifier.",
    slug_invalid: "Invalid page identifier.",
    name_missing: "Please enter a name.",
    name_too_long: "Name must be 64 characters or fewer.",
    email_missing: "Please enter an email address.",
    email_too_long: "That email address is too long.",
    body_missing: "Please write a comment.",
    body_too_long: "Comment must be 4000 characters or fewer.",
    parent_invalid: "That comment can't be replied to.",
    turnstile_unconfigured:
      "Comments aren't accepting submissions yet: the site owner still needs to set TURNSTILE_SECRET_KEY.",
    turnstile_failed:
      "Spam check failed. Please reload the page and try again.",
    subscribe_rate_limited: "Too many attempts — try again in a minute.",
    google_unconfigured:
      "Signing in isn't set up on this site yet: the site owner still needs to set GOOGLE_CLIENT_ID.",
    google_invalid: "That sign-in didn't check out. Please try again.",
  },
};

export type Catalogue = typeof en;

// Typed against `en`, so an untranslated key fails `npm run typecheck`.
const ko: Catalogue = {
  lang: "ko",
  ui: {
    loading: "댓글을 불러오는 중…",
    empty: "아직 댓글이 없습니다.",
    loadFailed: "댓글을 불러오지 못했습니다.",
    nameLabel: "이름",
    // Kept about as wide as the English label: the two fields share a grid row.
    emailLabel: "이메일 (아바타용, 공개되지 않음)",
    bodyLabel: "댓글",
    submit: "댓글 등록",
    posting: "등록 중…",
    posted: "댓글이 등록되었습니다. 감사합니다.",
    pending: "댓글이 등록되었습니다. 검토 후 게시됩니다.",
    postFailed: "댓글을 등록하지 못했습니다.",
    spamTimeout:
      "스팸 확인이 끝나지 않았습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.",
    missingSlug: "댓글: data-slug 속성이 없습니다.",
    // The generic carries its own subject particle; a name given in
    // data-owner-name can carry one too ("Serena님"), and reads fine without.
    owner: "주인이",
    hearted: (owner: string) => `${owner} 좋아합니다`,
    reply: "답글",
    replyTo: (name: string) => `${name}님에게 답글`,
    cancelReply: "취소",
  },
  subscribe: {
    checking: "확인 중…",
    saving: "저장 중…",
    subscribedState: "구독 중입니다.",
    unsubscribedState: "구독하고 있지 않습니다.",
    subscribe: "구독하기",
    unsubscribe: "구독 취소",
    failed: "처리하지 못했습니다. 다시 시도해 주세요.",
    unconfigured: "이 사이트는 아직 로그인을 설정하지 않았습니다.",
  },
  errors: {
    generic: "문제가 발생했습니다. 다시 시도해 주세요.",
    bad_json: "요청을 처리할 수 없습니다.",
    rate_limited: "너무 빠르게 작성하고 있습니다. 잠시 후 다시 시도해 주세요.",
    slug_missing: "페이지 식별자가 없습니다.",
    slug_invalid: "페이지 식별자가 올바르지 않습니다.",
    name_missing: "이름을 입력해 주세요.",
    name_too_long: "이름은 64자 이하여야 합니다.",
    email_missing: "이메일 주소를 입력해 주세요.",
    email_too_long: "이메일 주소가 너무 깁니다.",
    body_missing: "댓글 내용을 입력해 주세요.",
    body_too_long: "댓글은 4000자 이하여야 합니다.",
    parent_invalid: "이 댓글에는 답글을 달 수 없습니다.",
    turnstile_unconfigured:
      "아직 댓글을 받을 수 없습니다. 사이트 관리자가 TURNSTILE_SECRET_KEY를 설정해야 합니다.",
    turnstile_failed:
      "스팸 확인에 실패했습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.",
    subscribe_rate_limited: "시도가 너무 잦습니다. 잠시 후 다시 시도해 주세요.",
    google_unconfigured:
      "아직 로그인을 사용할 수 없습니다. 사이트 관리자가 GOOGLE_CLIENT_ID를 설정해야 합니다.",
    google_invalid: "로그인을 확인하지 못했습니다. 다시 시도해 주세요.",
  },
};

export const CATALOGUES: Record<Lang, Catalogue> = { en, ko };

/**
 * The first candidate naming a supported language. Region subtags are ignored
 * ("ko-KR" is Korean) and an unsupported tag is skipped rather than fatal.
 */
export function resolveStrings(
  ...candidates: (string | null | undefined)[]
): Catalogue {
  for (const candidate of candidates) {
    const tag = candidate?.trim().toLowerCase().split("-")[0];
    if (tag && tag in CATALOGUES) return CATALOGUES[tag as Lang];
  }
  return en;
}

/**
 * This bundle's translation of the code, or the server's own English prose for
 * a code it doesn't know — so a widget cached from before the API grew a new
 * failure still says something specific.
 */
export function errorText(
  strings: Catalogue,
  code: string,
  prose: string,
): string {
  const table: Record<string, string | undefined> = strings.errors;
  return table[code] ?? (prose || strings.errors.generic);
}
